import { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Camera, Undo2 } from "lucide-react";
import { Modal, In, Sel } from "@/pages/Projects";
import EvidenceGallery from "@/components/EvidenceGallery";

export default function CustodiesTab({ warehouses, products, projects, onChanged }) {
  const [custodies, setCustodies] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [returning, setReturning] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ warehouse_id: "", product_id: "", quantity: "", assignee_name: "", assignee_phone: "", project_id: "", notes: "" });
  const [files, setFiles] = useState([]);
  const [retForm, setRetForm] = useState({ quantity: "", condition: "bueno", notes: "" });
  const [retFiles, setRetFiles] = useState([]);

  const tools = products.filter((p) => ["herramienta", "equipo"].includes(p.item_type));

  const load = async () => {
    const { data } = await api.get("/custodies");
    setCustodies(data);
  };
  useEffect(() => { load(); }, []);

  const uploadPhotos = async (fl) => {
    const ids = [];
    for (const f of fl) {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      ids.push(data.id);
    }
    return ids;
  };

  const startNew = () => {
    setForm({ warehouse_id: warehouses[0]?.id || "", product_id: tools[0]?.id || "", quantity: "1", assignee_name: "", assignee_phone: "", project_id: "", notes: "" });
    setFiles([]);
    setOpenNew(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const photos = files.length ? await uploadPhotos(files) : [];
      await api.post("/custodies", {
        warehouse_id: form.warehouse_id, product_id: form.product_id, quantity: Number(form.quantity),
        assignee_name: form.assignee_name, assignee_phone: form.assignee_phone || null,
        project_id: form.project_id || null, notes: form.notes || null, photos,
      });
      toast.success("Resguardo entregado");
      setOpenNew(false); load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const startReturn = (c) => {
    const outstanding = c.quantity - (c.qty_returned || 0) - (c.qty_lost || 0);
    setReturning(c);
    setRetForm({ quantity: String(outstanding), condition: "bueno", notes: "" });
    setRetFiles([]);
  };

  const confirmReturn = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const photos = retFiles.length ? await uploadPhotos(retFiles) : [];
      await api.post(`/custodies/${returning.id}/return`, {
        quantity: Number(retForm.quantity), condition: retForm.condition, notes: retForm.notes || null, photos,
      });
      toast.success(retForm.condition === "perdido" ? "Pérdida registrada (no regresa a stock)" : "Devolución registrada");
      setReturning(null); load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  const statusChip = (c) => {
    const map = { entregado: ["EN RESGUARDO", "var(--yellow, #eab308)"], parcial: ["PARCIAL", "var(--cyan)"], cerrado: ["CERRADO", "var(--green)"] };
    const [label, color] = map[c.status] || [c.status, "var(--cyan)"];
    return <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm" style={{ border: `1px solid ${color}`, color }}>{label}</span>;
  };

  return (
    <div className="space-y-3" data-testid="custodies-tab">
      <div className="flex justify-between items-center">
        <span className="label-mono" style={{ color: "var(--cyan)" }}>// RESGUARDOS DE HERRAMIENTA Y EQUIPO · entrega y devolución con evidencia</span>
        <button data-testid="new-custody-btn" onClick={startNew} disabled={!warehouses.length || !tools.length} className="btn-primary px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1.5 disabled:opacity-50">
          <Plus className="w-3 h-3" /> Resguardo
        </button>
      </div>
      {!tools.length && <p className="text-xs text-slate-500">Marca productos como «herramienta» o «equipo» en el catálogo para poder entregarlos en resguardo.</p>}
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]" data-testid="custodies-table">
          <thead className="panel-header"><tr className="text-left">
            <th className="label-mono px-4 py-3">Folio</th><th className="label-mono px-4 py-3">Herramienta/Equipo</th>
            <th className="label-mono px-4 py-3">Resguardante</th><th className="label-mono px-4 py-3">Obra</th>
            <th className="label-mono px-4 py-3 text-right">Entregado</th><th className="label-mono px-4 py-3 text-right">Devuelto</th>
            <th className="label-mono px-4 py-3 text-right">Perdido</th><th className="label-mono px-4 py-3">Estado</th>
            <th className="label-mono px-4 py-3 text-right">Acciones</th>
          </tr></thead>
          <tbody>
            {custodies.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-slate-500">Sin resguardos.</td></tr>}
            {custodies.map((c) => (
              <tr key={c.id} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
                <td className="px-4 py-3 font-mono text-xs font-bold" style={{ color: "var(--cyan)" }}>{c.folio}</td>
                <td className="px-4 py-3"><span className="font-semibold text-slate-100">{c.product_name}</span> <span className="text-[10px] uppercase text-slate-500">{c.item_type}</span><div className="text-[10px] text-slate-500">{c.warehouse_name} · {fmtDate(c.created_at)}</div></td>
                <td className="px-4 py-3 text-xs text-slate-300">{c.assignee_name}{c.assignee_phone && <div className="text-slate-500">{c.assignee_phone}</div>}</td>
                <td className="px-4 py-3 text-xs text-slate-400">{c.project_label || "—"}</td>
                <td className="px-4 py-3 text-right font-mono">{c.quantity} {c.unit}</td>
                <td className="px-4 py-3 text-right font-mono" style={{ color: "var(--green)" }}>{c.qty_returned || 0}</td>
                <td className="px-4 py-3 text-right font-mono" style={{ color: c.qty_lost ? "var(--red)" : "inherit" }}>{c.qty_lost || 0}</td>
                <td className="px-4 py-3">{statusChip(c)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {(c.photos?.length > 0 || c.returns?.some((r) => r.photos?.length)) && (
                    <button data-testid={`custody-photos-${c.id}`} onClick={() => setGallery({ photos: [...(c.photos || []), ...(c.returns || []).flatMap((r) => r.photos || [])], title: c.folio })} className="p-1 mr-2 rounded-sm hover:bg-white/5" style={{ color: "var(--cyan)" }}><Camera className="w-4 h-4" /></button>
                  )}
                  {c.status !== "cerrado" && (
                    <button data-testid={`return-custody-${c.id}`} onClick={() => startReturn(c)} className="text-xs font-semibold hover:underline inline-flex items-center gap-1" style={{ color: "var(--green)" }}><Undo2 className="w-3.5 h-3.5" /> DEVOLVER</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openNew && (
        <Modal onClose={() => setOpenNew(false)} title="Entregar en resguardo">
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Sel label="Almacén" testId="cu-warehouse" value={form.warehouse_id} onChange={(v) => setForm({ ...form, warehouse_id: v })} options={warehouses.map((w) => ({ value: w.id, label: w.name }))} />
              <Sel label="Herramienta / equipo" testId="cu-product" value={form.product_id} onChange={(v) => setForm({ ...form, product_id: v })} options={tools.map((p) => ({ value: p.id, label: `${p.code} · ${p.name}` }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <In label="Cantidad" testId="cu-qty" type="number" value={form.quantity} onChange={(v) => setForm({ ...form, quantity: v })} />
              <Sel label="Obra (opcional)" testId="cu-project" value={form.project_id} onChange={(v) => setForm({ ...form, project_id: v })} options={[{ value: "", label: "—" }, ...projects.map((p) => ({ value: p.id, label: `${p.code} · ${p.name}` }))]} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <In label="Quién recibe (resguardante)" testId="cu-assignee" value={form.assignee_name} onChange={(v) => setForm({ ...form, assignee_name: v })} />
              <In label="Teléfono (opcional)" testId="cu-phone" value={form.assignee_phone} onChange={(v) => setForm({ ...form, assignee_phone: v })} required={false} />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Fotos de entrega</label>
              <input data-testid="cu-photos" type="file" accept="image/*" multiple onChange={(e) => setFiles([...e.target.files])} className="text-xs text-slate-400" />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Notas</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full input-cyber px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setOpenNew(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" disabled={saving} data-testid="save-custody-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">Entregar</button>
            </div>
          </form>
        </Modal>
      )}

      {returning && (
        <Modal onClose={() => setReturning(null)} title={`Devolución · ${returning.folio}`}>
          <form onSubmit={confirmReturn} className="space-y-4">
            <p className="text-sm text-slate-300">{returning.product_name} · resguardo de <span className="font-semibold">{returning.assignee_name}</span></p>
            <div className="grid grid-cols-2 gap-3">
              <In label={`Cantidad (${returning.unit})`} testId="ret-qty" type="number" value={retForm.quantity} onChange={(v) => setRetForm({ ...retForm, quantity: v })} />
              <Sel label="Condición" testId="ret-condition" value={retForm.condition} onChange={(v) => setRetForm({ ...retForm, condition: v })} options={[{ value: "bueno", label: "Bueno (regresa a stock)" }, { value: "dañado", label: "Dañado (regresa a stock)" }, { value: "perdido", label: "Perdido (no regresa)" }]} />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Fotos de recepción</label>
              <input data-testid="ret-photos" type="file" accept="image/*" multiple onChange={(e) => setRetFiles([...e.target.files])} className="text-xs text-slate-400" />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Notas</label>
              <textarea value={retForm.notes} onChange={(e) => setRetForm({ ...retForm, notes: e.target.value })} rows={2} className="w-full input-cyber px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setReturning(null)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" disabled={saving} data-testid="confirm-return-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">Registrar devolución</button>
            </div>
          </form>
        </Modal>
      )}

      {gallery && <EvidenceGallery photos={gallery.photos} title={gallery.title} onClose={() => setGallery(null)} />}
    </div>
  );
}
